'use client';

import React, { useEffect, useRef, useState } from 'react';
import {
  PropertiesTableContent,
  PropertiesTableValue,
} from '@/integrations/crystallize/__gql-codegen-types/catalogue/graphql';
import { useLocalization } from '@/ui/providers/localizationProvider';
import Question from '@/ui/components/tiles/CustomerSupportForm/components/Question';
import RequestForm from '@/ui/components/tiles/CustomerSupportForm/components/RequestForm';
import { ChevronLeftIcon } from '@heroicons/react/20/solid';
import { useTranslations } from 'next-intl';
import ExitPage from '@/ui/components/tiles/CustomerSupportForm/components/ExitPage';
import { usePathname, useSearchParams } from 'next/navigation';
import { PrefillFormValues } from '@/ui/components/tiles/CustomerSupportForm/types/prefillFormValues';
import { getExistingTicketData } from '@/server-functions/csForm/getTicketData';
import { buildTrackingId } from '@/utils/tracking-helper';

export type CustomerSupportProps = {
  tile: any;
  loadCSForm: (path: string, lang: string) => Promise<any>;
  loadCSFormOptions: (path: string) => Promise<string[]>;
  trackingPrefix?: string;
};

export default function CustomerSupportForm({
  tile,
  loadCSForm,
  loadCSFormOptions,
  trackingPrefix,
}: Readonly<CustomerSupportProps>) {
  const locale = useLocalization();
  const t = useTranslations('CSForm');
  const [loading, setLoading] = useState(true);
  const [csForm, setFormData] = useState<any>({});
  const [contactOptions, setContactOptions] = useState<Array<string>>([]);
  const [formHistory, setFormHistory] = useState<string[]>([]);
  const [pathForTicketDescription, setPathForTicketDescription] = useState<string[]>([]);
  const pathForTicketDescriptionRef = useRef<string[]>([]);
  const currentUrl = usePathname() || '';

  useEffect(() => {
    pathForTicketDescriptionRef.current = pathForTicketDescription;
  }, [pathForTicketDescription]);

  const searchParams = useSearchParams();

  const [prefillValues, setPrefillValues] = useState<PrefillFormValues>({
    firstName: searchParams.get('firstName'),
    lastName: searchParams.get('lastName'),
    email: searchParams.get('email'),
    ticketDescription: null,
  });

  const ticketId = searchParams.get('ticketId');

  const reloadCsForm = async (path: string, optionsPath?: string) => {
    setLoading(true);
    const loadedCsForm = await loadCSForm(path, locale.language);
    if (optionsPath) {
      setContactOptions(await loadCSFormOptions(optionsPath));
    }
    setFormData(loadedCsForm);
    formHistory.push(path);
    // eslint-disable-next-line no-restricted-globals
    history.pushState({ p: path }, path, '');
    setFormHistory(formHistory);
    setLoading(false);
  };

  const backHandler = async () => {
    if (formHistory.length < 2) {
      return;
    }
    const path = formHistory[formHistory.length - 2];
    formHistory.pop();
    formHistory.pop();
    setFormHistory(formHistory);

    if (path) {
      await reloadCsForm(path);
    }
  };

  const addToPathForTicketDescription = (question: string, answer: string) => {
    setPathForTicketDescription((prev) => [...prev, question, answer]);
  };

  const hasRunEffect = useRef(false);

  useEffect(() => {
    if (hasRunEffect.current) {
      return () => {};
    }
    window.addEventListener('popstate', async () => {
      const currentPathForTicketDescription = pathForTicketDescriptionRef.current;
      const newPathForTicketDescription = currentPathForTicketDescription.slice(0, -2);
      setPathForTicketDescription(newPathForTicketDescription);

      await backHandler();
    });
    hasRunEffect.current = true;
    let countryPath: PropertiesTableValue | undefined;
    let optionsPath: PropertiesTableValue | undefined;
    (tile['country-path'] as PropertiesTableContent)?.sections?.forEach((item) => {
      countryPath = item.properties?.find((k) => k.key === locale.country);
      if (!countryPath) {
        countryPath = item.properties?.find((k) => k.key === '*');
      }
    });
    (tile['country-options-path'] as PropertiesTableContent)?.sections?.forEach((item) => {
      optionsPath = item.properties?.find((k) => k.key === locale.country);
      if (!optionsPath) {
        optionsPath = item.properties?.find((k) => k.key === '*');
      }
    });
    if (countryPath?.value && optionsPath?.value) {
      reloadCsForm(countryPath.value, optionsPath.value);
    } else {
      setLoading(false);
    }

    if (ticketId && prefillValues.email) {
      const fetchTicketData = async () => {
        try {
          const updatedValues = await getExistingTicketData(ticketId, prefillValues);
          setPrefillValues(updatedValues);
        } catch (e) {
          console.error('failed to fetch description', e);
        }
      };

      fetchTicketData();
    }
    return () => {};
  }, []);

  useEffect(() => {
    if (formHistory.length > 1) {
      document.getElementById('csform-scrolltop')?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [formHistory.length]);

  if (loading) {
    return (
      <div className="flex h-40 items-center justify-center">
        <div className="mt-20 h-12 w-12 animate-spin rounded-full border-4 border-neutral-20 border-t-primary-100" />
      </div>
    );
  }

  const backButton = (
    <button
      type="button"
      className="mx-auto flex items-center font-semibold uppercase"
      onClick={() => {
        // eslint-disable-next-line no-restricted-globals
        history.back();
      }}
      data-tile-element-id={buildTrackingId(trackingPrefix, 'back')}
    >
      <ChevronLeftIcon className="h-4 w-4" />
      {t('Back')}
    </button>
  );

  switch (csForm?.catalogue?.shapeIdentifier) {
    case 'cs-form-question':
      return (
        <div id="csform-scrolltop" className="pb-10 pt-10">
          <Question
            components={csForm.catalogue.components}
            loadCsForm={reloadCsForm}
            addToPathForTicketDescription={addToPathForTicketDescription}
            trackingPrefix={trackingPrefix}
          />
          {formHistory.length > 1 && backButton}
        </div>
      );
    case 'cs-form-request-form':
      return (
        <div id="csform-scrolltop" className="pb-10 pt-10">
          <RequestForm
            components={csForm.catalogue.components}
            prefillValues={prefillValues}
            formPath={pathForTicketDescription}
            formUrl={currentUrl}
            contactOptions={contactOptions}
            existingTicketId={ticketId ? Number(ticketId) : undefined}
            trackingPrefix={trackingPrefix}
          />
          {formHistory.length > 1 && backButton}
        </div>
      );
    case 'cs-form-exit':
      return (
        <div id="csform-scrolltop" className="pb-10 pt-10">
          <ExitPage components={csForm.catalogue.components} />
          {formHistory.length > 1 && backButton}
        </div>
      );
    default:
      return null;
  }
}
